import {InfiniteData, useInfiniteQuery, UseInfiniteQueryResult} from '@tanstack/react-query';
import {clientVersionsApi} from '../api';
import {ClientVersionsFilter, ClientVersionsWithMetaModel} from '../model';
import {useCallback, useMemo, useState} from 'react';

type ClientVersionsState = {
  getInfiniteClientVersions: UseInfiniteQueryResult<InfiniteData<ClientVersionsWithMetaModel>, Error>;
  filters: ClientVersionsFilter;
  updateFilter: (newFilter: Partial<ClientVersionsFilter>) => void;
};

const DEFAULT_PAGE_SIZE = 25;

export const useClientVersions = (initialFilter: ClientVersionsFilter = {}): ClientVersionsState => {
  const [filters, setFilters] = useState<ClientVersionsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteClientVersions = useInfiniteQuery<
    ClientVersionsWithMetaModel,
    Error,
    InfiniteData<ClientVersionsWithMetaModel>,
    ['client-versions', 'infinite', Omit<ClientVersionsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['client-versions', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return clientVersionsApi.getClientVersions({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<ClientVersionsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  return {
    getInfiniteClientVersions,
    filters,
    updateFilter,
  };
};

